import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, first, map, Observable, of, Subscription } from 'rxjs';
import { ChartConfiguration } from 'src/app/pump-simulation/chartConfiguration';
import { createConfiguration, plotData } from 'src/app/shared/util';
import { ShareDataService } from '../../share-data.service';
import { CalculateCoefficientState } from '../../store/calculate-coefficient/calculate-coefficient.reducer';
import { selectCalculateCoefficient } from '../../store/calculate-coefficient/calculate-coefficient.selector';
import { IntermediateState } from '../../store/charts/intermediate-chart/intermediate-chart.reducer';
import { selectIntermediate } from '../../store/charts/intermediate-chart/intermediate-chart.selector';

declare const google: any;

const CQ_CHART_ID = 'cqChart';

@Component({
    selector: 'app-cq-chart',
    templateUrl: './cq-chart.component.html',
    styleUrls: ['./cq-chart.component.scss'],
    standalone: false
})
export class CqChartComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];
  xAxisToYaxisChart = null;
  showPlaceholder$: Observable<Boolean>;
  @ViewChild(CQ_CHART_ID, { static: true }) chartElement: ElementRef;

  constructor(
    private store: Store,
    private shareService: ShareDataService,
  ) { }

  ngOnInit() {
    const chartLoading = this.shareService.getChartItem().subscribe(data => {
      if (data.includes('mcq') || data.includes('ncq')) {
        this.showPlaceholder$ = of(true);
      }
    });
    this.subscriptions.push(chartLoading);

    const cqChart = this.store.pipe(
      select(selectIntermediate),
      map((data: IntermediateState) => data.intermediateData),
      distinctUntilChanged()
    ).subscribe(data => {
      if (data) {
        this.handleCQGraph();
      }
    });
    this.subscriptions.push(cqChart);
  }

  handleCQGraph() {
    const data = this.store.pipe(
      select(selectIntermediate),
      map((data: IntermediateState) => data.chartItems),
      first()
    ).subscribe(data => {
      if (data.includes('cqChart')) {
        this.showPlaceholder$ = of(true);
        this.createCQGraph();
      }
    });
    this.subscriptions.push(data);
  }

  createCQGraph() {

    const cqChartConfiguration = createConfiguration({ yAxis: 'CQ [m³/h]' });

    const cqGraph = combineLatest([
      this.store.pipe(
        select(selectCalculateCoefficient),
        map((data: CalculateCoefficientState) => data)
      ),
      this.store.pipe(
        select(selectIntermediate),
        map((data: IntermediateState) => data),
      ),
    ]).pipe(first()).subscribe(([measuredData, calculatedData]) => {

      const cqMeasuredData = new Map<number, number>();
      const cqCalculatedData = new Map<number, number>();
      const cqMeasuredDataMap = new Map();
      const cqCalculatedDataMap = new Map();

      for (const entry of measuredData.allCoefficientsData.intermediateCoefficents.entries()) {
        cqMeasuredData.set(entry[0], entry[1].cq);
        cqMeasuredDataMap.set(1, cqMeasuredData);
      }

      if (calculatedData.intermediateData) {

        for (const entry of calculatedData.intermediateData.entries()) {
          cqCalculatedData.set(entry[0], entry[1].cq);
          cqCalculatedDataMap.set(-1, cqCalculatedData);
        }
        this.showPlaceholder$ = of(false);
        google.charts.load('current', { packages: ['corechart'] });
        google.charts.setOnLoadCallback(this.drawChart(cqChartConfiguration, this.chartElement.nativeElement, cqCalculatedDataMap, cqMeasuredDataMap));
      }
    });
    this.subscriptions.push(cqGraph);
  }

  drawChart(
    chartConfiguration: ChartConfiguration,
    chart: ElementRef,
    calculation: Map<number, Map<number, number>>,
    measurements: Map<number, Map<number, number>>
  ) {
    return () => {
      let columnIndex = 1;
      let rowIndex = 0;
      const colors = chartConfiguration.colours;
      const data = new google.visualization.DataTable();
      data.addColumn('number', chartConfiguration.xAxisTitle);
      const options = chartConfiguration.options;

      let head;
      ({ head, rowIndex, columnIndex } = plotData(
        calculation, data, rowIndex, chartConfiguration,
        columnIndex, options, colors, 'calculated'));
      ({ head, rowIndex, columnIndex } = plotData(
        measurements, data, rowIndex, chartConfiguration,
        columnIndex, options, colors, 'measured'));

      this.xAxisToYaxisChart = new google.visualization.LineChart(chart);
      google.visualization.events.addListener(this.xAxisToYaxisChart, 'ready', () => {
        // remove loading
      });
      this.xAxisToYaxisChart.draw(data, options);
    };
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    if(this.xAxisToYaxisChart) {
      this.xAxisToYaxisChart.clearChart();
    }
    // this.store.dispatch(setIntermediateData({ data: null }));
  }
}
